/**
 * Serviço BFF (Backend for Frontend) para Recommendation Engine
 * Implementa o padrão de desacoplamento: Frontend → Data-Provider → Frontend → Recommendation-Engine
 */

interface ProviderData {
  status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
  data?: any;
  error_message?: string;
}

interface AnalysisRequest {
  social_id: string;
  document_type: 'CPF' | 'CNPJ';
  business_area: 'PLD' | 'CREDIT' | 'COMPLIANCE';
  provider_data: Record<string, ProviderData>;
  additional_data?: Record<string, any>;
}

interface AnalysisResponse {
  analysis_id: string;
  social_id: string;
  recommendation: 'APROVADO' | 'REPROVADO' | 'PENDENTE';
  risk_score: number;
  analysis_date: string;
  rules_applied: string[];
  message: string;
}

class RecommendationBFFService {
  private readonly API_KEY: string;
  private readonly DATA_PROVIDER_BASE_URL: string;
  private readonly RECOMMENDATION_ENGINE_BASE_URL: string;

  constructor() {
    this.API_KEY = process.env.REACT_APP_ROUTER_API_KEY || process.env.REACT_APP_API_KEY || '';
    const routerUrl = (process.env.REACT_APP_ROUTER_API_URL || '').replace(/\/$/, ''); // Remove trailing slash
    this.DATA_PROVIDER_BASE_URL = `${routerUrl}/data-provider`;
    this.RECOMMENDATION_ENGINE_BASE_URL = `${routerUrl}/recommendation-engine`;
  }

  /**
   * Fluxo completo BFF: busca dados + análise de recomendação
   */
  async analyzeDocument(socialId: string, documentType: 'CPF' | 'CNPJ', businessArea: 'PLD' | 'CREDIT' | 'COMPLIANCE' = 'PLD'): Promise<AnalysisResponse> {
    try {
      // Buscar dados de todos os providers
      const providerData = await this.fetchAllProviderData(socialId, documentType);

      // Enviar dados consolidados para recommendation-engine
      const analysis = await this.sendToRecommendationEngine({
        social_id: socialId,
        document_type: documentType,
        business_area: businessArea,
        provider_data: providerData
      });
      return analysis;
    } catch (error) {
      console.error('[RecommendationBFFService] Erro na análise BFF:', error);
      throw error;
    }
  }

  /**
   * Busca dados de todos os providers disponíveis
   */
  private async fetchAllProviderData(socialId: string, documentType: 'CPF' | 'CNPJ'): Promise<Record<string, ProviderData>> {
    const providers = ['INOVAMIND', 'ESCAVADOR', 'QUOD', 'QUOD-PJ'];
    const providerData: Record<string, ProviderData> = {};

    // Buscar dados de cada provider em paralelo
    const promises = providers.map(async (provider) => {
      try {
        const data = await this.fetchProviderData(socialId, documentType, provider);
        providerData[provider] = {
          status: 'SUCCESS',
          data: data
        };
      } catch (error) {
        providerData[provider] = {
          status: 'ERROR',
          error_message: error instanceof Error ? error.message : 'Erro desconhecido'
        };
        // Log de erro apenas
        console.error(`[RecommendationBFFService] Erro ao buscar dados do provider ${provider}:`, error);
      }
    });

    await Promise.allSettled(promises);
    return providerData;
  }

  /**
   * Busca dados de um provider específico
   */
  private async fetchProviderData(socialId: string, documentType: 'CPF' | 'CNPJ', provider: string): Promise<any> {
    const endpoint = this.getProviderEndpoint(provider, documentType);
    const url = `${this.DATA_PROVIDER_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        document: socialId,
        document_type: documentType
      })
    });

    if (!response.ok) {
      throw new Error(`${provider} API error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Mapeia provider para endpoint correto
   */
  private getProviderEndpoint(provider: string, documentType: 'CPF' | 'CNPJ'): string {
    const endpoints: Record<string, string> = {
      'INOVAMIND': '/inovamind/analyze',
      'ESCAVADOR': '/escavador/analyze',
      'QUOD': documentType === 'CPF' ? '/quod/analyze' : '/quod-pj/analyze',
      'QUOD-PJ': '/quod-pj/analyze'
    };

    return endpoints[provider] || `/providers/${provider.toLowerCase()}/analyze`;
  }

  /**
   * Retry com exponential backoff
   */
  private async retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    initialDelay: number = 1000
  ): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;

        // Não retenta em erros 4xx (exceto 429)
        if (error.message?.includes('400') ||
          error.message?.includes('401') ||
          error.message?.includes('403') ||
          error.message?.includes('404')) {
          throw error;
        }

        // Última tentativa, lança erro
        if (attempt === maxRetries) {
          throw error;
        }

        // Calcula delay com exponential backoff
        const delay = initialDelay * Math.pow(2, attempt);
        // Log apenas em caso de erro
        console.warn(`[RecommendationBFFService] Retry ${attempt + 1}/${maxRetries} após ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }

  /**
   * Envia dados consolidados para recommendation-engine
   * Método público para uso em telas que precisam análise direta
   */
  async sendToRecommendationEngine(request: AnalysisRequest): Promise<AnalysisResponse> {
    return this.retryWithBackoff(async () => {
      const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-with-data`;

      // Log mínimo: apenas erro

      // Timeout de 60 segundos para cold start do Lambda
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-api-key': this.API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(request),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Recommendation Engine error: ${response.status} ${response.statusText} - ${errorText}`);
        }

        return await response.json();
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Timeout ao aguardar resposta do recommendation-engine (60s). O Lambda pode estar iniciando (cold start).');
        }
        throw error;
      }
    });
  }

  /**
   * Análise de lote completo usando endpoint batch (1 única requisição)
   * Usa dados já processados do lote ao invés de refazer chamadas aos providers
   * 
   * IMPORTANTE: Mantém todos os reports originais para a tela de detalhes
   * A unificação por documento é feita apenas no frontend da tela de recomendações
   */
  async analyzeBatch(batchId: string): Promise<any> {
    try {
      // Buscar dados do lote no data-provider (já inclui reports processados)
      const batchData = await this.fetchBatchData(batchId);

      // Preparar payload batch com todos os documentos
      if (batchData.reports && Array.isArray(batchData.reports)) {
        let documents: any[];
        
        // ✅ ESCAVADOR: Agrupar processos por documento
        if (batchData.provider === 'ESCAVADOR') {
          const documentMap = new Map<string, any[]>();
          
          // Agrupar reports por documento
          batchData.reports.forEach((report: any) => {
            const document = report.document || report.social_id;
            if (!documentMap.has(document)) {
              documentMap.set(document, []);
            }
            
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt',
              'backend_recommendation', 'backend_reasons', 'backend_risk_score',
              'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
              'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            
            const reportData: any = {};
            Object.keys(report).forEach(key => {
              if (!excludeFields.includes(key)) {
                reportData[key] = report[key];
              }
            });
            
            documentMap.get(document)!.push(reportData);
          });
          
          // Criar documentos com array de reports
          documents = Array.from(documentMap.entries()).map(([document, reports]) => ({
            social_id: document,
            document_type: document.replace(/\D/g, '').length === 11 ? 'CPF' : 'CNPJ',
            business_area: 'PLD',
            provider_data: {
              ESCAVADOR: {
                status: 'SUCCESS',
                data: { reports }
              }
            }
          }));
          
          console.log(`✅ ESCAVADOR: ${documents.length} documentos únicos com ${batchData.reports.length} processos`);
        } else {
          // ✅ INOVAMIND/QUOD: Manter lógica atual (1 report = 1 documento)
          documents = batchData.reports.map((report: any) => {
            const providerData: Record<string, ProviderData> = {};
            const excludeFields = ['document', 'name', 'status', 'statusFormatted', 'createdAt', 'updatedAt',
              'backend_recommendation', 'backend_reasons', 'backend_risk_score',
              'backend_analysis_id', 'backend_document_type', 'reportType', 'reportTypeId',
              'batchReportId', 'attempts', 'maxAttempts', 'events', 'analysis_status', 'pep_reason'];
            const reportData: any = {};
            Object.keys(report).forEach(key => {
              if (!excludeFields.includes(key)) {
                reportData[key] = report[key];
              }
            });
            const providerName = batchData.provider || 'UNKNOWN';
            providerData[providerName] = {
              status: 'SUCCESS',
              data: reportData
            };
            return {
              social_id: report.document || report.social_id,
              document_type: report.document_type || 'CNPJ',
              business_area: 'PLD',
              provider_data: providerData
            };
          });
        }

        // Enviar documentos com chunking para evitar timeout
        const batchResults = await this.sendBatchToRecommendationEngineWithChunking(
          documents,
          200,
          (processed, total) => {
            // Log de progresso reduzido
            if (processed % 50 === 0 || processed === total) {
              console.info(`[RecommendationBFFService] Progresso: ${processed}/${total} (${Math.round(processed / total * 100)}%)`);
            }
          }
        );

        // Enriquecer reports com resultados
        if (batchData.provider === 'ESCAVADOR') {
          // ESCAVADOR: Propagar resultado do documento para todos os processos
          batchData.reports.forEach((report: any) => {
            const document = report.document || report.social_id;
            const result = batchResults.results.find((r: any) => r.social_id === document);
            
            if (result) {
              report.backend_recommendation = result.recommendation;
              report.backend_reasons = result.rules_applied;
              report.backend_risk_score = result.risk_score;
              report.backend_analysis_id = result.analysis_id;
              report.backend_credit_flag = result.credit_flag;
            }
          });
        } else {
          // INOVAMIND/QUOD: Manter lógica atual (1:1)
          batchData.reports.forEach((report: any, index: number) => {
            const result = batchResults.results[index];
            if (result) {
              report.backend_recommendation = result.recommendation;
              report.backend_reasons = result.rules_applied;
              report.backend_risk_score = result.risk_score;
              report.backend_analysis_id = result.analysis_id;
              report.backend_credit_flag = result.credit_flag;
            }
          });
        }
      }
      return batchData;

    } catch (error) {
      console.error('❌ Erro na análise batch do lote:', error);
      throw error;
    }
  }

  /**
   * Envia lote completo para recommendation-engine (1 única requisição)
   * Método público para uso em telas que precisam análise batch direta
   */
  async sendBatchToRecommendationEngine(documents: any[]): Promise<any> {
    return this.retryWithBackoff(async () => {
      const url = `${this.RECOMMENDATION_ENGINE_BASE_URL}/recommendations/analyze-batch`;

      // Log mínimo: apenas erro

      // Timeout de 120 segundos para processar lote grande
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'x-api-key': this.API_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ documents }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Batch analysis error: ${response.status} ${response.statusText} - ${errorText}`);
        }

        const result = await response.json();
        console.log(`✅ Batch processado: ${result.total_processed} documentos, ${result.total_errors} erros`);

        return result;

      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('Timeout ao processar batch (120s). Tente com menos documentos ou aguarde.');
        }
        throw error;
      }
    });
  }

  /**
   * Envia lote grande em chunks para evitar timeout do API Gateway (29s)
   * Processa N documentos por vez com progresso
   */
  async sendBatchToRecommendationEngineWithChunking(
    documents: any[],
    chunkSize: number = 200,
    onProgress?: (processed: number, total: number) => void,
    concurrency: number = 4
  ): Promise<any> {
    const allResults: any[] = [];
    let totalProcessed = 0;
    let totalErrors = 0;
    const totalChunks = Math.ceil(documents.length / chunkSize);

    // Log mínimo: apenas erro

    // Dividir documentos em chunks
    const chunks = [];
    for (let i = 0; i < documents.length; i += chunkSize) {
      chunks.push(documents.slice(i, i + chunkSize));
    }

    // Processamento paralelo limitado
    let processedCount = 0;
    const processChunk = async (chunk: any[], chunkNumber: number) => {
      try {
        const chunkResult = await this.sendBatchToRecommendationEngine(chunk);
        allResults.push(...chunkResult.results);
        totalProcessed += chunkResult.total_processed;
        totalErrors += chunkResult.total_errors;
        processedCount += chunk.length;
        if (onProgress) {
          onProgress(processedCount, documents.length);
        }
      } catch (error) {
        console.error(`[RecommendationBFFService] Erro no chunk ${chunkNumber + 1}/${totalChunks}:`, error);
        totalErrors += chunk.length;
        allResults.push(...chunk.map(doc => ({
          social_id: doc.social_id,
          document_type: doc.document_type,
          recommendation: 'ERRO',
          rules_applied: [],
          risk_score: 0,
          error: 'Chunk processing failed'
        })));
      }
    };

    // Fila de execução paralela limitada
    const queue: Promise<void>[] = [];
    let idx = 0;
    while (idx < chunks.length) {
      while (queue.length < concurrency && idx < chunks.length) {
        queue.push(processChunk(chunks[idx], idx));
        idx++;
      }
      await Promise.race(queue);
      // Remove a primeira promessa resolvida da fila
      queue.shift();
    }
    // Espera todos terminarem
    await Promise.all(queue);

    console.log(`✅ Processamento completo: ${totalProcessed}/${documents.length} processados, ${totalErrors} erros`);

    return {
      results: allResults,
      total_processed: totalProcessed,
      total_errors: totalErrors
    };
  }

  /**
   * Busca dados do lote no data-provider
   */
  private async fetchBatchData(batchId: string): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/${batchId}/page-view`;

    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Batch data error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Se retornou erro interno, lançar exceção
    if (data.error) {
      throw new Error(`API error: ${data.error}`);
    }

    console.log('📦 Dados do lote retornados:', data);
    return data;
  }

  /**
   * Lista todos os lotes disponíveis (usa data-provider diretamente)
   */
  async fetchBatchList(): Promise<any> {
    // Para listagem de lotes, usar data-provider diretamente é correto
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list`;

    const authToken = localStorage.getItem('authToken');
    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Lista lotes com filtros (compatível com PageList)
   */
  async fetchBatchListWithFilters(params: URLSearchParams): Promise<any> {
    const url = `${this.DATA_PROVIDER_BASE_URL}/batch-reports/page-list?${params.toString()}`;

    const response = await fetch(url, {
      headers: {
        'x-api-key': this.API_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Batch list error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  }
}

// Instância singleton
export const recommendationBFFService = new RecommendationBFFService();

// Export da classe para testes
export { RecommendationBFFService };
